import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import AuthModal from 'components/AuthModal/AuthModal';
import { getVisitorStats } from 'service/VisitorStatsService';
import useChatUnread from 'hooks/useChatUnread';
import styles from './LeftSidebar.module.scss';

// ── SVG Icons ────────────────────────────────────────────────────────────────

const HomeIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.55 2.532a2.25 2.25 0 012.9 0l7.75 6.563A2.25 2.25 0 0122 10.818V20a2 2 0 01-2 2h-4a1 1 0 01-1-1v-5h-6v5a1 1 0 01-1 1H4a2 2 0 01-2-2v-9.182a2.25 2.25 0 01.8-1.724l7.75-6.562z"/>
  </svg>
);

const FeedbackIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.36 5.07L2 22l4.93-1.36A9.96 9.96 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
  </svg>
);

const ServicesIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="7.5" height="7.5" rx="2.5"/>
    <rect x="13.5" y="3" width="7.5" height="7.5" rx="2.5"/>
    <rect x="3" y="13.5" width="7.5" height="7.5" rx="2.5"/>
    <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2.5"/>
  </svg>
);

const ChatIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    {/* back bubble */}
    <path d="M17 12h2c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2H7c-1.1 0-2 .9-2 2v2h8c1.66 0 3 1.34 3 3v3z" opacity="0.55"/>
    {/* front bubble with tail */}
    <path d="M15 8H3c-1.1 0-2 .9-2 2v8l3.64-3H15c1.1 0 2-.9 2-2v-3c0-1.1-.9-2-2-2z"/>
  </svg>
);

const AIIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C9.5 2 8 3.8 8 5.5c0 .6.2 1.2.5 1.7C6.5 7.9 5 9.8 5 12c0 1.3.5 2.5 1.3 3.4C5.5 16.2 5 17.3 5 18.5 5 20.4 6.6 22 8.5 22c.9 0 1.7-.3 2.3-.9.4.1.8.1 1.2.1s.8 0 1.2-.1c.6.5 1.4.9 2.3.9 1.9 0 3.5-1.6 3.5-3.5 0-1.2-.5-2.3-1.3-3.1.8-.9 1.3-2.1 1.3-3.4 0-2.2-1.5-4.1-3.5-4.8.3-.5.5-1.1.5-1.7C16 3.8 14.5 2 12 2zm0 7a3 3 0 110 6 3 3 0 010-6z"/>
  </svg>
);

const NcoreHubIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(12,12)">
      <path d="M0-2Q2-5,0-8Q-2-5,0-2Z"/>
      <path d="M0-2Q2-5,0-8Q-2-5,0-2Z" transform="rotate(72)"/>
      <path d="M0-2Q2-5,0-8Q-2-5,0-2Z" transform="rotate(144)"/>
      <path d="M0-2Q2-5,0-8Q-2-5,0-2Z" transform="rotate(216)"/>
      <path d="M0-2Q2-5,0-8Q-2-5,0-2Z" transform="rotate(288)"/>
      <circle cx="0" cy="0" r="1.8"/>
      <path d="M0 2Q-0.8 5,-0.8 8L0.8 8Q0.8 5,0 2Z"/>
    </g>
  </svg>
);

const SpeedIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2a10 10 0 100 20A10 10 0 0012 2zm4.24 6.34l-4.6 4.6a1.5 1.5 0 11-2.12-2.12l4.6-4.6a1 1 0 011.42 0 1 1 0 01.7 2.12zM5.5 12a1 1 0 110-2 1 1 0 010 2zm2.04-4.46a1 1 0 110-2 1 1 0 010 2zM12 5.5a1 1 0 110-2 1 1 0 010 2zm4.46 2.04a1 1 0 110-2 1 1 0 010 2zM18.5 12a1 1 0 110-2 1 1 0 010 2z"/>
  </svg>
);

const AboutIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
);

const ContactIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
  </svg>
);

const TermsIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"/>
    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
  </svg>
);

const InstallIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
  </svg>
);

const ProfileIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
  </svg>
);

const PersonalPanelIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 3.25A1.25 1.25 0 008.25 9.5h2.5A1.25 1.25 0 0012 8.25v-.5A1.25 1.25 0 0010.75 6.5h-2.5A1.25 1.25 0 007 7.75v.5zm5 0A1.25 1.25 0 0013.25 9.5h2.5A1.25 1.25 0 0017 8.25v-.5A1.25 1.25 0 0015.75 6.5h-2.5A1.25 1.25 0 0012 7.75v.5zM7 13.75A1.25 1.25 0 008.25 15h2.5A1.25 1.25 0 0012 13.75v-.5A1.25 1.25 0 0010.75 12h-2.5A1.25 1.25 0 007 13.25v.5zm5.95-1.42a.9.9 0 011.17-.47l.45.2.2-.45a.9.9 0 111.64.73l-.2.45.45.2a.9.9 0 11-.73 1.64l-.45-.2-.2.45a.9.9 0 11-1.64-.73l.2-.45-.45-.2a.9.9 0 01-.44-1.17z"/>
  </svg>
);

const FavoritesIcon = () => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6-1.41 1.41zM13 6l1.41-1.41L20 10.59V13.4L14.41 19 13 17.59l5-5-5-5zm0 0"/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6 1.41-1.41zM11 6L9.59 4.59 4 10.18V13l5.59 5.59L11 17.18l-5-5 5-5zm0 0"/>
  </svg>
);

// Service sub-menu icons
const ServerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2"/>
    <rect x="2" y="14" width="20" height="8" rx="2"/>
    <line x1="6" y1="6" x2="6.01" y2="6" strokeWidth="3"/>
    <line x1="6" y1="18" x2="6.01" y2="18" strokeWidth="3"/>
  </svg>
);
const CommunityIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="7" r="4"/><circle cx="17" cy="11" r="3"/>
    <path d="M1 21c0-3.3 3.6-6 8-6s8 2.7 8 6"/><path d="M17 21c0-2 1.3-4 4-4.5"/>
  </svg>
);
const MovieIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <line x1="8" y1="4" x2="8" y2="20"/><line x1="16" y1="4" x2="16" y2="20"/>
    <line x1="2" y1="10" x2="22" y2="10"/><line x1="2" y1="16" x2="22" y2="16"/>
    <line x1="2" y1="4" x2="22" y2="4"/>
  </svg>
);
const MusicIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
  </svg>
);
const AnimeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
    <line x1="9" y1="9" x2="9.01" y2="9" strokeWidth="3"/>
    <line x1="15" y1="9" x2="15.01" y2="9" strokeWidth="3"/>
  </svg>
);
const MediaIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);
const ShopIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 01-8 0"/>
  </svg>
);
const ITIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
  </svg>
);
const DonateIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
  </svg>
);

// ── Services list ─────────────────────────────────────────────────────────────
const services = [
  { id: 'servers',    name: 'Серверы',         Icon: ServerIcon  },
  { id: 'community',  name: 'Сообщество',      Icon: CommunityIcon },
  { id: 'movie',      name: 'Фильмы',          Icon: MovieIcon   },
  { id: 'music',      name: 'Музыка',          Icon: MusicIcon   },
  { id: 'anime',      name: 'Аниме',           Icon: AnimeIcon   },
  { id: 'media',      name: 'Медиа',           Icon: MediaIcon   },
  { id: 'shop',       name: 'Магазин',         Icon: ShopIcon    },
  { id: 'it',         name: 'NCORE IT',        Icon: ITIcon      },
];

// ── Tooltip ───────────────────────────────────────────────────────────────────
function Tooltip({ label, children, visible, parentRef, className }) {
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (visible && parentRef?.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setCoords({
        top: rect.top + rect.height / 2,
        left: rect.right + 12
      });
    }
  }, [visible, parentRef]);

  if (!visible || !coords) return null;

  return createPortal(
    <div
      className={className || styles.tooltip}
      style={{
        position: 'fixed',
        left: coords.left,
        top: coords.top,
        transform: 'translateY(-50%)',
        zIndex: 99999
      }}
    >
      {children || label}
    </div>,
    document.body
  );
}

// ── SidebarItem ───────────────────────────────────────────────────────────────
function SidebarItem({ icon: Icon, label, active, onClick, expanded, children, badge }) {
  const [hovered, setHovered] = useState(false);
  const itemRef = useRef(null);

  return (
    <div
      ref={itemRef}
      className={`${styles.item} ${active ? styles.itemActive : ''}`}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className={`${styles.itemIcon} ${active ? styles.iconActive : ''} ${hovered ? styles.iconHovered : ''}`} style={{ position: 'relative' }}>
        <Icon />
        {badge > 0 && (
          <span className={styles.sidebarUnreadDot}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      {expanded && (
        <span className={`${styles.itemLabel} ${active ? styles.labelActive : ''}`}>{label}</span>
      )}
      {!expanded && <Tooltip label={label} visible={hovered && !expanded} parentRef={itemRef} />}
      {children}
    </div>
  );
}

// ── LeftSidebar ───────────────────────────────────────────────────────────────
const isInStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function LeftSidebar({ expanded, onToggle, fullScreenMobile = false, onCloseMobile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const totalUnread = useChatUnread(!!user);
  const [authOpen, setAuthOpen] = useState(false);
  const [profileHovered, setProfileHovered] = useState(false);
  const [onlineHovered, setOnlineHovered] = useState(false);
  const [visitsHovered, setVisitsHovered] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [stats, setStats] = useState({ online_count: 0, total_visits: 0 });
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [pwaInstalled, setPwaInstalled] = useState(
    () => isInStandalone() || !!localStorage.getItem('ncore_pwa_dismissed')
  );

  // Refs for tooltips
  const profileRef = useRef(null);
  const onlineRef = useRef(null);
  const visitsRef = useRef(null);

  // Scroll logic
  const scrollRef = useRef(null);
  const scrollInterval = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const checkScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setCanScrollUp(scrollTop > 0);
    // tolerance of 1px
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 1);
  }, []);

  useEffect(() => {
    // Initial check and on resize
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [checkScroll, stats, expanded, fullScreenMobile]); 

  const startScroll = (direction) => {
    if (scrollInterval.current) return;
    scrollInterval.current = setInterval(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop += direction * 5;
        checkScroll();
      }
    }, 16);
  };

  const stopScroll = () => {
    if (scrollInterval.current) {
      clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    }
  };

  useEffect(() => {
    if (pwaInstalled) return;
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setPwaInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [pwaInstalled]);

  const handlePwaInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') { setPwaInstalled(true); localStorage.setItem('ncore_pwa_dismissed', '1'); }
    setDeferredPrompt(null);
  };

  const is = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const avatar = user?.avatar || user?.profile?.photo || null;

  useEffect(() => {
    const handler = (e) => { if (e.detail) setAuthOpen(true); };
    window.addEventListener('openAuth', handler);
    return () => window.removeEventListener('openAuth', handler);
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getVisitorStats();
        if (data) setStats({ online_count: data.online_count || 0, total_visits: data.total_visits || 0 });
      } catch {}
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fmtNum = (n) => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  };

  const handleItemClick = (action) => {
    action();
    if (fullScreenMobile && onCloseMobile) {
      onCloseMobile();
    }
  };

  return (
    <>
      <aside className={`${styles.sidebar} ${(expanded || fullScreenMobile) ? styles.expanded : ''} ${fullScreenMobile ? styles.fullScreenMobile : ''}`}>

        {fullScreenMobile && (
          <div className={styles.mobileCloseBtn} onClick={onCloseMobile}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
        )}

        <div className={styles.scrollContent} ref={scrollRef} onScroll={checkScroll}>
          {/* ── Stats ── */}
          <div className={styles.statsBlock}>
            <div
              className={styles.statRow}
              ref={onlineRef}
              style={{ position: 'relative' }}
              onMouseEnter={() => setOnlineHovered(true)}
              onMouseLeave={() => setOnlineHovered(false)}
            >
              <div className={styles.statCol}>
                <div className={styles.pulseWrapper}>
                  <span className={styles.pulseOuter} />
                  <span className={styles.pulseRing} />
                  <span className={styles.pulseDot} />
                </div>
                <span className={styles.statNum}>{fmtNum(stats.online_count)}</span>
              </div>
              {(expanded || fullScreenMobile) && <span className={styles.statLbl}>Онлайн</span>}
              
              {!expanded && !fullScreenMobile && (
                <Tooltip visible={onlineHovered} parentRef={onlineRef} className={styles.statTooltip}>
                  <span className={styles.statTooltipNum}>{stats.online_count.toLocaleString()}</span>
                  <span className={styles.statTooltipLbl}>сейчас онлайн</span>
                </Tooltip>
              )}
            </div>
            
            <div
              className={styles.statRow}
              ref={visitsRef}
              style={{ position: 'relative' }}
              onMouseEnter={() => setVisitsHovered(true)}
              onMouseLeave={() => setVisitsHovered(false)}
            >
              <div className={styles.statCol}>
                <span className={styles.statIcon}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.5 18.5L9.5 12.5L13.5 16.5L22 6.92L20.59 5.5L13.5 13.5L9.5 9.5L2 17L3.5 18.5Z"/>
                  </svg>
                </span>
                <span className={styles.statNum}>{fmtNum(stats.total_visits)}</span>
              </div>
              {(expanded || fullScreenMobile) && <span className={styles.statLbl}>Визитов</span>}
              
              {!expanded && !fullScreenMobile && (
                <Tooltip visible={visitsHovered} parentRef={visitsRef} className={styles.statTooltip}>
                  <span className={styles.statTooltipNum}>{stats.total_visits.toLocaleString()}</span>
                  <span className={styles.statTooltipLbl}>всего визитов</span>
                </Tooltip>
              )}
            </div>
          </div>

          <div className={styles.divider} />

          {/* ── Profile (desktop position) ── */}
          {!fullScreenMobile && (
            <div
              ref={profileRef}
              className={`${styles.profileItem} ${is('/profile') ? styles.itemActive : ''}`}
              onMouseEnter={() => setProfileHovered(true)}
              onMouseLeave={() => setProfileHovered(false)}
              onClick={() => {
                if (user) handleItemClick(() => navigate('/profile'));
                else handleItemClick(() => setAuthOpen(true));
              }}
            >
              <span className={`${styles.itemIcon} ${is('/profile') ? styles.iconActive : ''} ${profileHovered ? styles.iconHovered : ''}`}>
                {user && avatar && imgOk ? (
                  <img
                    src={avatar}
                    alt="avatar"
                    className={styles.avatarImg}
                    onError={() => setImgOk(false)}
                  />
                ) : (
                  <ProfileIcon />
                )}
              </span>
              {(expanded || fullScreenMobile) && (
                <span className={`${styles.itemLabel} ${is('/profile') ? styles.labelActive : ''}`}>
                  {user ? (user.username || user?.profile?.name || 'Профиль') : 'Войти'}
                </span>
              )}
              {!expanded && !fullScreenMobile && (
                 <Tooltip label={user ? (user.username || 'Профиль') : 'Войти'} visible={profileHovered} parentRef={profileRef} />
              )}
            </div>
          )}

          {/* ── Toggle expand/collapse (hidden on mobile) ── */}
          {!fullScreenMobile && (
            <div
              className={styles.toggleBtn}
              onClick={onToggle}
              title={expanded ? 'Свернуть' : 'Развернуть'}
            >
              <span className={styles.itemIcon}>
                {expanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </span>
              {(expanded || fullScreenMobile) && <span className={styles.toggleLabel}>Свернуть</span>}
            </div>
          )}

          <div className={styles.divider} />

          {!fullScreenMobile && user && (
            <SidebarItem
              icon={PersonalPanelIcon}
              label="Мой хаб"
              active={is('/personal-panel')}
              onClick={() => handleItemClick(() => navigate('/personal-panel'))}
              expanded={expanded || fullScreenMobile}
            />
          )}

          {/* ── Home ── */}
          <SidebarItem
            icon={HomeIcon}
            label="Главная"
            active={location.pathname === '/'}
            onClick={() => handleItemClick(() => navigate('/'))}
            expanded={expanded || fullScreenMobile}
          />

          {fullScreenMobile && (
            <SidebarItem
              icon={ProfileIcon}
              label={user ? (user.username || user?.profile?.name || 'Профиль') : 'Войти'}
              active={is('/profile')}
              onClick={() => {
                if (user) handleItemClick(() => navigate('/profile'));
                else handleItemClick(() => setAuthOpen(true));
              }}
              expanded={expanded || fullScreenMobile}
            />
          )}

          <SidebarItem
            icon={FeedbackIcon}
            label="Обратная связь"
            active={is('/feedback')}
            onClick={() => handleItemClick(() => navigate('/feedback'))}
            expanded={expanded || fullScreenMobile}
          />

          <div className={styles.divider} />

          <SidebarItem
            icon={ServicesIcon}
            label="Сервисы"
            active={is('/services')}
            onClick={() => handleItemClick(() => navigate('/services'))}
            expanded={expanded || fullScreenMobile}
          />

          <SidebarItem
            icon={ChatIcon}
            label="Чаты"
            active={is('/chats')}
            onClick={() => handleItemClick(() => navigate('/chats'))}
            expanded={expanded || fullScreenMobile}
            badge={totalUnread}
          />

          <div className={`${styles.divider} ${styles.hideOnDesktop}`} />

          <div className={styles.hideOnDesktop}>
            <SidebarItem
              icon={FavoritesIcon}
              label="Наши любимчики"
              active={is('/favorites')}
              onClick={() => handleItemClick(() => navigate('/favorites'))}
              expanded={expanded || fullScreenMobile}
            />
          </div>

          <div className={styles.divider} />

          {/* <SidebarItem
            icon={AIIcon}
            label="AI"
            active={is('/ai')}
            onClick={() => handleItemClick(() => navigate('/ai'))}
            expanded={expanded || fullScreenMobile}
          /> */}

          <SidebarItem
            icon={NcoreHubIcon}
            label="NcoreHub"
            active={is('/ncoreboard')}
            onClick={() => handleItemClick(() => navigate('/ncoreboard'))}
            expanded={expanded || fullScreenMobile}
          />

          <SidebarItem
            icon={SpeedIcon}
            label="Скорость"
            active={is('/speedtest')}
            onClick={() => handleItemClick(() => navigate('/speedtest'))}
            expanded={expanded || fullScreenMobile}
          />

          <div className={styles.divider} />

          <SidebarItem
            icon={AboutIcon}
            label="О нас"
            active={is('/about')}
            onClick={() => handleItemClick(() => navigate('/about'))}
            expanded={expanded || fullScreenMobile}
          />

          <SidebarItem
            icon={ContactIcon}
            label="Контакты"
            active={is('/contacts')}
            onClick={() => handleItemClick(() => navigate('/contacts'))}
            expanded={expanded || fullScreenMobile}
          />

          <div className={styles.spacer} />

          <div className={styles.divider} />

          {!pwaInstalled && deferredPrompt && (
            <SidebarItem
              icon={InstallIcon}
              label="Установить"
              active={false}
              onClick={handlePwaInstall}
              expanded={expanded || fullScreenMobile}
            />
          )}

          <SidebarItem
            icon={TermsIcon}
            label="Соглашение"
            active={is('/terms-and-conditions')}
            onClick={() => handleItemClick(() => navigate('/terms-and-conditions'))}
            expanded={expanded || fullScreenMobile}
          />

          <SidebarItem
            icon={() => (
              <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.883 13.65l-2.96-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.91-.001-.001-.001 0 0 0z"/>
              </svg>
            )}
            label="Telegram"
            active={false}
            onClick={() => handleItemClick(() => window.open('https://t.me/kyrgyz_cybersport_federation', '_blank', 'noopener,noreferrer'))}
            expanded={expanded || fullScreenMobile}
          />
        </div>

        {/* Scroll Indicators */}
        {!fullScreenMobile && canScrollUp && (
           <div 
             className={`${styles.scrollIndicator} ${styles.top}`}
             onMouseEnter={() => startScroll(-1)}
             onMouseLeave={stopScroll}
           >
              <svg className={styles.scrollIndicatorIcon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
              </svg>
           </div>
        )}
        
        {!fullScreenMobile && canScrollDown && (
           <div 
             className={styles.scrollIndicator}
             onMouseEnter={() => startScroll(1)}
             onMouseLeave={stopScroll}
           >
              <svg className={styles.scrollIndicatorIcon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
              </svg>
           </div>
        )}

      </aside>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
}
